import { useState, useCallback } from 'react';
import { useToast } from '@chakra-ui/react';
import { toStorageImageApiSegment } from '../utils/storageImagePath';

const getAuthToken = () => {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('Token não encontrado');
  }
  return token;
};

const createAuthHeaders = () => {
  try {
    const token = getAuthToken();
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  } catch (error) {
    console.error('Erro ao criar headers de autenticação:', error);
    throw error;
  }
};

// Função para verificar se o token ainda é válido
const isTokenValid = () => {
  const token = localStorage.getItem('token');
  if (!token) return false;

  try {
    // Decodificar o JWT para verificar expiração (opcional)
    const payload = JSON.parse(atob(token.split('.')[1]));
    const now = Date.now() / 1000;
    return payload.exp > now;
  } catch (error) {
    console.error('Erro ao validar token:', error);
    return false;
  }
};

export const useInfractions = () => {
  const [infractions, setInfractions] = useState([]);
  const [filteredInfractions, setFilteredInfractions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchStats, setSearchStats] = useState(null); // Novo estado para estatísticas
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    hasMore: false,
    activeFilter: null
  });

  const toast = useToast();

  // Atualizar a função fetchImageWithAuth para usar o mesmo padrão dos outros componentes
  const fetchImageWithAuth = useCallback(async (imagePath) => {
    try {
      if (!imagePath) return '';

      const filename = toStorageImageApiSegment(imagePath, 'infractions');
      if (!filename) return '';

      const token = localStorage.getItem('token');
      if (!token) {
        console.warn('Token não encontrado para carregar imagem');
        return '';
      }

      const response = await fetch(`/api/infractions/image/${filename}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          console.error('Token inválido ou expirado');
          localStorage.removeItem('token');
          window.location.href = '/login';
          return '';
        }
        throw new Error(`Erro ao buscar imagem: ${response.status}`);
      }

      const blob = await response.blob();
      return URL.createObjectURL(blob);
    } catch (error) {
      console.error('Erro ao carregar imagem:', error);
      return '';
    }
  }, []);

  // Atualizar as funções que processam infrações para usar o padrão correto
  const fetchInfractions = useCallback(async (loadMore = false, userLevel = 3) => {
    const isInitialLoad = !loadMore;

    if (isInitialLoad) {
      setLoading(true);
      setSearchStats(null); // Limpar estatísticas ao buscar infrações normais
    } else {
      setLoadingMore(true);
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Token não encontrado');
      }

      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      const page = loadMore ? pagination.currentPage + 1 : 1;
      const limit = pagination.limit || 20;

      let url;
      // Corrigindo as rotas baseadas no backend
      console.log(`[DEBUG] userLevel recebido: ${userLevel}, tipo: ${typeof userLevel}`);
      
      if (userLevel <= 2) {
        // Rota para infrações revisadas pelo usuário atual
        url = `/api/infractions/reviewed-by-me?page=${page}&limit=${limit}&sortField=reviewed_at&sortDirection=DESC`;
        console.log(`[DEBUG] Usando rota para usuário nível ${userLevel}: ${url}`);
      } else {
        // Rota geral de infrações
        url = `/api/infractions?page=${page}&limit=${limit}&sortField=detected_at&sortDirection=DESC`;
        console.log(`[DEBUG] Usando rota geral para usuário nível ${userLevel}: ${url}`);
      }

      console.log(`Buscando infrações com URL: ${url}, userLevel: ${userLevel}`);

      const response = await fetch(url, { headers });

      console.log(`[DEBUG] Response status: ${response.status}`);

      if (!response.ok) {
        if (response.status === 401) {
          console.log('[DEBUG] Token inválido, redirecionando para login');
          localStorage.removeItem('token');
          window.location.href = '/login';
          return;
        }
        if (response.status === 403) {
          console.log('[DEBUG] Acesso negado - possível problema de nível de usuário');
          throw new Error(`Acesso negado. Verifique suas permissões. Status: ${response.status}`);
        }
        throw new Error(`Erro ao buscar infrações: ${response.status}`);
      }

      const responseData = await response.json();
      const infractionsData = responseData.infractions || responseData;
      const hasMore = responseData.totalPages ? page < responseData.totalPages : false;

      console.log(`[DEBUG] Infrações encontradas: ${infractionsData.length}`);
      console.log(`[DEBUG] Total de páginas: ${responseData.totalPages}, página atual: ${page}`);
      console.log(`[DEBUG] Primeira infração:`, infractionsData[0]);

      // Processar imagens com autenticação adequada
      const infractionsWithImages = await Promise.all(infractionsData.map(async (infraction) => {
        if (infraction.image_path) {
          const imageUrl = await fetchImageWithAuth(infraction.image_path);
          return { ...infraction, imageUrl };
        }
        return infraction;
      }));

      if (loadMore) {
        setInfractions(prev => [...prev, ...infractionsWithImages]);
        setFilteredInfractions(prev => [...prev, ...infractionsWithImages]);
      } else {
        setInfractions(infractionsWithImages);
        setFilteredInfractions(infractionsWithImages);
      }

      setPagination(prev => ({
        ...prev,
        currentPage: page,
        hasMore,
        totalPages: responseData.totalPages || 1,
        activeFilter: userLevel <= 2 ? 'reviewedByMe' : 'normal'
      }));

    } catch (error) {
      console.error('Erro ao buscar infrações:', error);
      toast({
        title: "Erro ao carregar infrações",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [pagination.currentPage, pagination.limit, fetchImageWithAuth, toast, setInfractions, setFilteredInfractions, setPagination, setLoading, setLoadingMore]);

  // Função para buscar infrações das últimas 24h
  const fetchLast24HoursInfractions = useCallback(async (onlyMine = false) => {
    setLoading(true);
    setSearchStats(null); // Limpar estatísticas ao buscar últimas 24h
    try {
      const token = localStorage.getItem('token');
      const url = `/api/infractions/last24hours?page=1&limit=20&onlyMine=${onlyMine}`;

      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Erro ao buscar infrações das últimas 24h');

      const responseData = await response.json();
      const infractionsData = responseData.infractions || responseData;

      const infractionsWithImages = await Promise.all(infractionsData.map(async (infraction) => {
        if (infraction.image_path) {
          const imageUrl = await fetchImageWithAuth(infraction.image_path);
          return { ...infraction, imageUrl };
        }
        return infraction;
      }));

      setInfractions(infractionsWithImages);
      setFilteredInfractions(infractionsWithImages);

      setPagination(prev => ({
        ...prev,
        currentPage: 1,
        hasMore: responseData.totalPages ? 1 < responseData.totalPages : false,
        activeFilter: 'last24h'
      }));

    } catch (error) {
      console.error('Erro ao processar infrações das últimas 24h:', error);
      toast({
        title: "Erro ao buscar infrações das últimas 24h",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }, [fetchImageWithAuth, toast]);

  const correctInfractionPlate = useCallback(async (id, plate, vehicleType) => {
    const token = localStorage.getItem('token');
    const current = infractions.find((infraction) => infraction.id === id)
      || filteredInfractions.find((infraction) => infraction.id === id);
    const response = await fetch(`/api/infractions/${id}/status`, {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        status: current?.status || 'pending',
        plate
      })
    });
    if (!response.ok) {
      throw new Error('Erro ao corrigir a placa');
    }

    const apply = (infraction) => (
      infraction.id === id
        ? {
          ...infraction,
          plate,
          registeredVehicleType: vehicleType || infraction.registeredVehicleType,
          vehicle_type: vehicleType || infraction.vehicle_type
        }
        : infraction
    );
    setInfractions((prev) => prev.map(apply));
    setFilteredInfractions((prev) => prev.map(apply));
  }, [infractions, filteredInfractions]);

  const updateInfractionStatus = useCallback(async (id, status) => {
    try {
      const token = localStorage.getItem('token');
      console.log(`Atualizando status da infração ${id} para ${status}...`);

      const updateToast = toast({
        title: "Atualizando infração...",
        description: "Aguarde enquanto processamos sua mudança",
        status: "info",
        duration: null,
        isClosable: false,
      });

      // Rota correta baseada no backend
      const response = await fetch(`/api/infractions/${id}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status })
      });

      if (!response.ok) throw new Error('Erro ao atualizar status');

      setInfractions(prev =>
        prev.map(inf => inf.id === id ? { ...inf, status } : inf)
      );

      setFilteredInfractions(prev =>
        prev.map(inf => inf.id === id ? { ...inf, status } : inf)
      );

      if (updateToast) toast.close(updateToast);

      toast({
        title: "Status atualizado",
        description: `Infração marcada como ${status}`,
        status: "success",
        duration: 2000,
        isClosable: true,
      });

    } catch (error) {
      console.error('Erro ao atualizar status:', error);
      toast({
        title: "Erro ao atualizar status",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  }, [toast]);

  // Função para buscar infrações com filtros avançados
  const searchInfractions = useCallback(async (filters) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      let queryParams = new URLSearchParams();

      // CORREÇÃO: Validar valores antes de adicionar aos parâmetros
      if (filters.plate && typeof filters.plate === 'string' && filters.plate.trim() !== '') {
        queryParams.append('plate', filters.plate.trim());
      }
      
      // Sempre enviar plateContains como string válida
      queryParams.append('plateContains', filters.plateContains === true ? 'true' : 'false');
      
      if (filters.cameraId && filters.cameraId !== '' && filters.cameraId !== 'undefined') {
        queryParams.append('cameraId', filters.cameraId);
      }
      
      if (filters.algorithmId && filters.algorithmId !== '' && filters.algorithmId !== 'undefined') {
        queryParams.append('algorithmId', filters.algorithmId);
      }
      
      if (filters.startDate && filters.startDate !== '' && filters.startDate !== 'undefined') {
        queryParams.append('startDate', filters.startDate);
      }
      
      if (filters.endDate && filters.endDate !== '' && filters.endDate !== 'undefined') {
        queryParams.append('endDate', filters.endDate);
      }
      
      if (filters.status && filters.status !== '' && filters.status !== 'undefined') {
        queryParams.append('status', filters.status);
      }

      if (filters.infractionClass && filters.infractionClass !== '' && filters.infractionClass !== 'undefined') {
        queryParams.append('infractionClass', filters.infractionClass);
      }

      if (filters.reviewedBy && filters.reviewedBy !== '' && filters.reviewedBy !== 'undefined') {
        queryParams.append('reviewedBy', filters.reviewedBy);
      }

      // CORREÇÃO: Garantir que page e limit sejam números válidos
      const page = 1;
      const limit = pagination.limit || 20;
      
      queryParams.append('page', page.toString());
      queryParams.append('limit', limit.toString());

      const url = `/api/infractions/search?${queryParams.toString()}`;
      console.log('URL de pesquisa avançada:', url);

      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Erro ao buscar infrações com filtros');

      const responseData = await response.json();
      const infractionsData = responseData.infractions || [];

      const infractionsWithImages = await Promise.all(infractionsData.map(async (infraction) => {
        if (infraction.image_path) {
          const imageUrl = await fetchImageWithAuth(infraction.image_path);
          return { ...infraction, imageUrl };
        }
        return infraction;
      }));

      setInfractions(infractionsWithImages);
      setFilteredInfractions(infractionsWithImages);

      // Atualizar estatísticas se retornadas pelo backend
      if (responseData.stats) {
        setSearchStats(responseData.stats);
      } else {
        // Fallback: calcular estatísticas localmente se não retornadas pelo backend
        const stats = {
          total: infractionsWithImages.length,
          confirmed: infractionsWithImages.filter(inf => inf.status === 'confirmed').length,
          rejected: infractionsWithImages.filter(inf => inf.status === 'rejected').length,
          pending: infractionsWithImages.filter(inf => inf.status === 'pending').length
        };
        setSearchStats(stats);
      }

      setPagination(prev => ({
        ...prev,
        page: 1,
        hasMore: responseData.totalPages ? 1 < responseData.totalPages : false,
        activeFilter: 'search'
      }));

    } catch (error) {
      console.error('Erro ao buscar infrações com filtros:', error);
      toast({
        title: "Erro ao buscar infrações",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }, [fetchImageWithAuth, toast, pagination.limit, setPagination, setInfractions, setFilteredInfractions, setLoading]);

  // Função para carregar mais infrações com filtros de busca
  const searchInfractionsLoadMore = useCallback(async (filters) => {
    if (loadingMore) return;

    try {
      setLoadingMore(true);
  
      const token = localStorage.getItem('token');
      const page = pagination.page + 1;
      const limit = pagination.limit || 20;
      
      let queryParams = new URLSearchParams();
      
      // CORREÇÃO: Validar valores antes de adicionar aos parâmetros
      if (filters.plate && typeof filters.plate === 'string' && filters.plate.trim() !== '') {
        queryParams.append('plate', filters.plate.trim());
      }
      
      // Sempre enviar plateContains como string válida
      queryParams.append('plateContains', filters.plateContains === true ? 'true' : 'false');
      
      if (filters.cameraId && filters.cameraId !== '' && filters.cameraId !== 'undefined') {
        queryParams.append('cameraId', filters.cameraId);
      }
      
      if (filters.algorithmId && filters.algorithmId !== '' && filters.algorithmId !== 'undefined') {
        queryParams.append('algorithmId', filters.algorithmId);
      }
      
      if (filters.startDate && filters.startDate !== '' && filters.startDate !== 'undefined') {
        queryParams.append('startDate', filters.startDate);
      }
      
      if (filters.endDate && filters.endDate !== '' && filters.endDate !== 'undefined') {
        queryParams.append('endDate', filters.endDate);
      }
      
      if (filters.status && filters.status !== '' && filters.status !== 'undefined') {
        queryParams.append('status', filters.status);
      }

      if (filters.infractionClass && filters.infractionClass !== '' && filters.infractionClass !== 'undefined') {
        queryParams.append('infractionClass', filters.infractionClass);
      }

      if (filters.reviewedBy && filters.reviewedBy !== '' && filters.reviewedBy !== 'undefined') {
        queryParams.append('reviewedBy', filters.reviewedBy);
      }

      // CORREÇÃO: Garantir que page e limit sejam números válidos
      queryParams.append('page', page.toString());
      queryParams.append('limit', limit.toString());

      const url = `/api/infractions/search?${queryParams.toString()}`;
      console.log('Carregando mais resultados de busca avançada:', url);

      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Erro ao buscar mais infrações');

      const responseData = await response.json();
      const infractionsData = responseData.infractions || [];
      const hasMore = responseData.totalPages ? page < responseData.totalPages : false;

      const infractionsWithImages = await Promise.all(infractionsData.map(async (infraction) => {
        if (infraction.image_path) {
          const imageUrl = await fetchImageWithAuth(infraction.image_path);
          return { ...infraction, imageUrl };
        }
        return infraction;
      }));

      setInfractions(prev => [...prev, ...infractionsWithImages]);
      setFilteredInfractions(prev => [...prev, ...infractionsWithImages]);

      setPagination(prev => ({
        ...prev,
        page: page,
        hasMore: hasMore
      }));

    } catch (error) {
      console.error('Erro ao carregar mais infrações:', error);
      toast({
        title: "Erro ao carregar mais infrações",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, pagination, fetchImageWithAuth, toast, setInfractions, setFilteredInfractions, setPagination, setLoadingMore]);

  // Função para buscar infrações revisadas por mim
  const fetchReviewedByMeInfractions = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const url = `/api/infractions/reviewed-by-me?page=1&limit=20`;

      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Erro ao buscar infrações revisadas por mim');

      const responseData = await response.json();
      const infractionsData = responseData.infractions || responseData;

      const infractionsWithImages = await Promise.all(infractionsData.map(async (infraction) => {
        if (infraction.image_path) {
          const imageUrl = await fetchImageWithAuth(infraction.image_path);
          return { ...infraction, imageUrl };
        }
        return infraction;
      }));

      setInfractions(infractionsWithImages);
      setFilteredInfractions(infractionsWithImages);

      setPagination(prev => ({
        ...prev,
        currentPage: 1,
        hasMore: responseData.totalPages ? 1 < responseData.totalPages : false,
        activeFilter: 'reviewedByMe'
      }));

    } catch (error) {
      console.error('Erro ao processar infrações revisadas por mim:', error);
      toast({
        title: "Erro ao buscar infrações revisadas por mim",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }, [fetchImageWithAuth, toast]);

  // Função para carregar mais infrações revisadas por mim
  const fetchReviewedByMeLoadMore = useCallback(async () => {
    if (loadingMore) return;

    setLoadingMore(true);
    try {
      const token = localStorage.getItem('token');
      const page = pagination.currentPage + 1;
      const url = `/api/infractions/reviewed-by-me?page=${page}&limit=20`;

      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Erro ao carregar mais infrações revisadas por mim');

      const responseData = await response.json();
      const infractionsData = responseData.infractions || responseData;

      const infractionsWithImages = await Promise.all(infractionsData.map(async (infraction) => {
        if (infraction.image_path) {
          const imageUrl = await fetchImageWithAuth(infraction.image_path);
          return { ...infraction, imageUrl };
        }
        return infraction;
      }));

      setInfractions(prev => [...prev, ...infractionsWithImages]);
      setFilteredInfractions(prev => [...prev, ...infractionsWithImages]);

      setPagination(prev => ({
        ...prev,
        currentPage: page,
        hasMore: responseData.totalPages ? page < responseData.totalPages : false,
        activeFilter: 'reviewedByMe'
      }));

    } catch (error) {
      console.error('Erro ao carregar mais infrações revisadas por mim:', error);
      toast({
        title: "Erro ao carregar mais infrações",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoadingMore(false);
    }
  }, [pagination.currentPage, fetchImageWithAuth, toast]);

  // Função para carregar mais infrações das últimas 24h
  const fetchLast24HoursLoadMore = useCallback(async (onlyMine = false) => {
    if (loadingMore) return;

    setLoadingMore(true);
    try {
      const token = localStorage.getItem('token');
      const page = pagination.currentPage + 1;
      const url = `/api/infractions/last24hours?page=${page}&limit=20&onlyMine=${onlyMine}`;

      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Erro ao carregar mais infrações das últimas 24h');

      const responseData = await response.json();
      const infractionsData = responseData.infractions || responseData;

      const infractionsWithImages = await Promise.all(infractionsData.map(async (infraction) => {
        if (infraction.image_path) {
          const imageUrl = await fetchImageWithAuth(infraction.image_path);
          return { ...infraction, imageUrl };
        }
        return infraction;
      }));

      setInfractions(prev => [...prev, ...infractionsWithImages]);
      setFilteredInfractions(prev => [...prev, ...infractionsWithImages]);

      setPagination(prev => ({
        ...prev,
        currentPage: page,
        hasMore: responseData.totalPages ? page < responseData.totalPages : false,
        activeFilter: 'last24h'
      }));

    } catch (error) {
      console.error('Erro ao carregar mais infrações das últimas 24h:', error);
      toast({
        title: "Erro ao carregar mais infrações",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoadingMore(false);
    }
  }, [pagination.currentPage, fetchImageWithAuth, toast, loadingMore]);

  // Função para carregar mais infrações (paginação)
  const loadMoreInfractions = useCallback(async (userLevel = 3) => {
    if (loadingMore || !pagination.hasMore) return;

    try {
      setLoadingMore(true);
      const token = localStorage.getItem('token');
      const page = pagination.currentPage + 1;
      const limit = pagination.limit || 20;

      // Determinar a URL baseada no filtro ativo
      let url;
      switch (pagination.activeFilter) {
        case 'reviewedByMe':
          url = `/api/infractions/reviewed-by-me?page=${page}&limit=${limit}`;
          break;
        case 'last24h':
          const onlyMine = userLevel <= 2;
          url = `/api/infractions/last-24h?page=${page}&limit=${limit}&onlyMine=${onlyMine}`;
          break;
        case 'search':
          // Para busca com filtros, não devemos usar esta função
          console.warn('loadMoreInfractions chamado para busca com filtros - use searchInfractionsLoadMore');
          return;
        default:
          // Busca normal baseada no nível do usuário
          if (userLevel <= 2) {
            url = `/api/infractions/reviewed-by-me?page=${page}&limit=${limit}`;
          } else {
            url = `/api/infractions?page=${page}&limit=${limit}&sortField=detected_at&sortDirection=DESC`;
          }
      }

      console.log(`Carregando mais infrações: ${url}`);

      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Erro ao carregar mais infrações');

      const responseData = await response.json();
      const infractionsData = responseData.infractions || responseData;
      const hasMore = responseData.totalPages ? page < responseData.totalPages : false;

      const infractionsWithImages = await Promise.all(infractionsData.map(async (infraction) => {
        if (infraction.image_path) {
          const imageUrl = await fetchImageWithAuth(infraction.image_path);
          return { ...infraction, imageUrl };
        }
        return infraction;
      }));

      // Atualizar ambos os arrays
      setInfractions(prev => [...prev, ...infractionsWithImages]);
      setFilteredInfractions(prev => [...prev, ...infractionsWithImages]);

      setPagination(prev => ({
        ...prev,
        currentPage: page,
        hasMore,
        totalPages: responseData.totalPages || 1
      }));

    } catch (error) {
      console.error('Erro ao buscar infrações:', error);
      toast({
        title: "Erro ao carregar infrações",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoadingMore(false);
    }
  }, [pagination.currentPage, pagination.limit, pagination.hasMore, pagination.activeFilter, fetchImageWithAuth, toast, loadingMore, setInfractions, setFilteredInfractions, setPagination, setLoadingMore]);

  // Função para exportar dados da busca
  const exportSearchData = useCallback(async (filters) => {
    try {
      const token = getAuthToken();
      
      const response = await fetch('/api/infractions/export', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ filters })
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('token');
          window.location.href = '/login';
          return;
        }
        throw new Error('Erro ao exportar dados');
      }

      // Criar um blob com os dados do arquivo ZIP
      const blob = await response.blob();
      
      // Extrair o nome do arquivo do header Content-Disposition
      const contentDisposition = response.headers.get('content-disposition');
      let filename = 'infractions_export.zip';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      // Criar um link de download temporário
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      
      // Limpar recursos
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast({
        title: "Exportação concluída",
        description: "Arquivo ZIP baixado com sucesso",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

    } catch (error) {
      console.error('Erro ao exportar dados:', error);
      toast({
        title: "Erro na exportação",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  }, [toast]);

  // Função para exportar dados em PDF com imagens
  const exportToPDF = useCallback(async (filters, infractions, stats) => {
    try {
      const jsPDF = (await import('jspdf')).default;
      
      // Buscar TODAS as infrações que correspondem aos filtros para exportação
      console.log('Buscando todas as infrações para exportação PDF...');
      
      const token = getAuthToken();
      let queryParams = new URLSearchParams();

      // Construir query params com os filtros
      if (filters.plate && typeof filters.plate === 'string' && filters.plate.trim() !== '') {
        queryParams.append('plate', filters.plate.trim());
      }
      
      queryParams.append('plateContains', filters.plateContains === true ? 'true' : 'false');
      
      if (filters.cameraId && filters.cameraId !== '' && filters.cameraId !== 'undefined') {
        queryParams.append('cameraId', filters.cameraId);
      }
      
      if (filters.algorithmId && filters.algorithmId !== '' && filters.algorithmId !== 'undefined') {
        queryParams.append('algorithmId', filters.algorithmId);
      }
      
      if (filters.startDate && filters.startDate !== '' && filters.startDate !== 'undefined') {
        queryParams.append('startDate', filters.startDate);
      }
      
      if (filters.endDate && filters.endDate !== '' && filters.endDate !== 'undefined') {
        queryParams.append('endDate', filters.endDate);
      }
      
      if (filters.status && filters.status !== '' && filters.status !== 'undefined') {
        queryParams.append('status', filters.status);
      }

      if (filters.reviewedBy && filters.reviewedBy !== '' && filters.reviewedBy !== 'undefined') {
        queryParams.append('reviewedBy', filters.reviewedBy);
      }

      // Buscar sem limite de página para exportação (ou com limite alto)
      queryParams.append('page', '1');
      queryParams.append('limit', '1000'); // Limite alto para buscar mais dados

      const url = `/api/infractions/search?${queryParams.toString()}`;
      console.log('URL para buscar dados completos:', url);

      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error('Erro ao buscar dados completos para exportação');
      }

      const responseData = await response.json();
      const allInfractions = responseData.infractions || [];
      const exportStats = responseData.stats || stats;

      console.log(`Exportando ${allInfractions.length} infrações de um total de ${exportStats.total}`);

      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 20;
      let yPosition = margin;

      // Função para adicionar nova página se necessário
      const checkPageBreak = (neededSpace) => {
        if (yPosition + neededSpace > pageHeight - margin) {
          doc.addPage();
          yPosition = margin;
          return true;
        }
        return false;
      };

      // Função para converter imagem para base64
      const imageToBase64 = (imgUrl) => {
        return new Promise((resolve) => {
          if (!imgUrl || imgUrl === '') {
            resolve(null);
            return;
          }
          
          const img = new Image();
          img.crossOrigin = 'anonymous';
          img.onload = function() {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            canvas.width = this.naturalWidth;
            canvas.height = this.naturalHeight;
            ctx.drawImage(this, 0, 0);
            resolve(canvas.toDataURL('image/jpeg', 0.8));
          };
          img.onerror = function() {
            resolve(null);
          };
          img.src = imgUrl;
        });
      };

      // Título do relatório
      doc.setFontSize(20);
      doc.setFont('helvetica', 'bold');
      doc.text('Relatório de Infrações - Fiscalizer', pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 15;

      // Data de geração
      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      const now = new Date();
      doc.text(`Gerado em: ${now.toLocaleDateString('pt-BR')} às ${now.toLocaleTimeString('pt-BR')}`, pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 15;

      // Filtros aplicados
      if (filters && Object.keys(filters).length > 0) {
        checkPageBreak(30);
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.text('Filtros Aplicados:', margin, yPosition);
        yPosition += 10;

        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        
        Object.entries(filters).forEach(([key, value]) => {
          if (value && value.toString().trim() !== '' && typeof value !== 'boolean') {
            let filterText = '';
            switch (key) {
              case 'status': filterText = `Status: ${value}`; break;
              case 'startDate': filterText = `Data Inicial: ${new Date(value).toLocaleDateString('pt-BR')}`; break;
              case 'endDate': filterText = `Data Final: ${new Date(value).toLocaleDateString('pt-BR')}`; break;
              case 'plate': filterText = `Placa: ${value}`; break;
              case 'reviewedBy': filterText = `Revisor: ${value}`; break;
              default: filterText = `${key}: ${value}`;
            }
            if (filterText) {
              checkPageBreak(8);
              doc.text(`• ${filterText}`, margin + 5, yPosition);
              yPosition += 8;
            }
          }
        });
        yPosition += 10;
      }

      // Estatísticas resumidas
      if (exportStats) {
        checkPageBreak(40);
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.text('Resumo Estatístico:', margin, yPosition);
        yPosition += 15;

        doc.setFontSize(12);
        doc.setFont('helvetica', 'normal');
        
        // Criar tabela de estatísticas
        const statsData = [
          ['Total de Infrações', exportStats.total.toString()],
          ['Confirmadas', exportStats.confirmed.toString()],
          ['Rejeitadas', exportStats.rejected.toString()],
          ['Pendentes', exportStats.pending.toString()]
        ];

        statsData.forEach(([label, value]) => {
          checkPageBreak(10);
          doc.text(`${label}: ${value}`, margin + 5, yPosition);
          yPosition += 10;
        });
        yPosition += 10;
      }

      // Lista de infrações com imagens
      if (allInfractions && allInfractions.length > 0) {
        checkPageBreak(20);
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.text('Detalhes das Infrações:', margin, yPosition);
        yPosition += 15;

        // Limitar a 100 infrações para evitar PDFs muito grandes
        const limitedInfractions = allInfractions.slice(0, 100);
        
        for (let i = 0; i < limitedInfractions.length; i++) {
          const infraction = limitedInfractions[i];
          
          // Verificar se precisa de nova página (considerando espaço para imagem)
          checkPageBreak(100);

          doc.setFontSize(12);
          doc.setFont('helvetica', 'bold');
          doc.text(`Infração #${infraction.id}`, margin, yPosition);
          yPosition += 10;

          doc.setFontSize(10);
          doc.setFont('helvetica', 'normal');

          // Informações da infração
          const infractionInfo = [
            `Placa: ${infraction.plate || 'N/A'}`,
            `Tipo de Veículo: ${infraction.vehicle_type || 'N/A'}`,
            `Status: ${infraction.status || 'N/A'}`,
            `Confiança: ${infraction.confidence || 'N/A'}%`,
            `Data/Hora: ${infraction.detected_at ? new Date(infraction.detected_at).toLocaleString('pt-BR') : 'N/A'}`,
            `Câmera: ${infraction.Camera?.name || 'N/A'}`,
            `Algoritmo: ${infraction.Algorithm?.name || 'N/A'}`
          ];

          infractionInfo.forEach(info => {
            checkPageBreak(8);
            doc.text(info, margin + 5, yPosition);
            yPosition += 8;
          });

          // Adicionar imagem se disponível
          if (infraction.imageUrl) {
            try {
              const base64Image = await imageToBase64(infraction.imageUrl);
              if (base64Image) {
                checkPageBreak(80);
                yPosition += 5;
                
                // Calcular dimensões da imagem mantendo proporção
                const maxWidth = 80;
                const maxHeight = 60;
                doc.addImage(base64Image, 'JPEG', margin + 5, yPosition, maxWidth, maxHeight);
                yPosition += maxHeight + 10;
              }
            } catch (error) {
              console.warn(`Erro ao processar imagem da infração ${infraction.id}:`, error);
            }
          }

          yPosition += 15; // Espaço entre infrações

          // Mostrar progresso no console
          if (i % 10 === 0) {
            console.log(`Processando infração ${i + 1} de ${limitedInfractions.length}`);
          }
        }

        // Adicionar nota se foram limitadas as infrações
        if (allInfractions.length > 100) {
          checkPageBreak(20);
          doc.setFontSize(10);
          doc.setFont('helvetica', 'italic');
          doc.text(`Nota: Este relatório mostra as primeiras 100 infrações de um total de ${allInfractions.length}.`, margin, yPosition);
          doc.text('Para o relatório completo, considere aplicar filtros mais específicos.', margin, yPosition + 8);
        }
      }

      // Salvar o PDF
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const filename = `relatorio_infracoes_${timestamp}.pdf`;
      doc.save(filename);

      toast({
        title: "PDF gerado com sucesso",
        description: `Arquivo ${filename} baixado com ${exportStats?.total || allInfractions.length} infrações filtradas`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });

    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
      toast({
        title: "Erro ao gerar PDF",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  }, [toast]);

  return {
    infractions,
    filteredInfractions,
    loading,
    loadingMore,
    pagination,
    searchStats, // Adicionar estatísticas ao retorno
    setInfractions,
    setFilteredInfractions,
    setPagination,
    setLoading: setLoading,
    setLoadingMore: setLoadingMore,
    fetchInfractions,
    fetchLast24HoursInfractions,
    fetchLast24HoursLoadMore,
    updateInfractionStatus,
    correctInfractionPlate,
    fetchImageWithAuth,
    searchInfractions,
    searchInfractionsLoadMore,
    fetchReviewedByMeInfractions,
    fetchReviewedByMeLoadMore,
    loadMoreInfractions,
    exportSearchData,
    exportToPDF
  };
};